import { useParams } from "react-router-dom";
import { useBitcoiners, useBooks } from "@/hooks/use-sheets-data";
import { useAnalytics } from "@/hooks/use-analytics";
import { Card, CardContent } from "@/components/ui/card";
import { Link } from "react-router-dom";
import { XLogo } from "@/components/icons/x-logo";

export function Person() {
  const { slug } = useParams();
  const { data: bitcoiners = [], isLoading: bitcoinersLoading } = useBitcoiners();
  const { data: books = [], isLoading: booksLoading } = useBooks();
  useAnalytics();

  // Find person by matching the slug
  const person = bitcoiners.find((b) => b.slug === slug);
  console.log('Debug - Found person details:', {
    name: person?.name,
    slug: person?.slug,
    allFields: person
  });
  
  // Find books recommended by this person by checking the recommendedBy field
  const recommendedBooks = books.filter(book => {
    const normalizedSlug = slug?.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
    console.log(`Debug - Checking book "${book.title}":`, {
      recommendedBy: book.recommendedBy,
      normalizedRecommenders: book.recommendedBy?.map(r => 
        r.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '')
      ),
      personSlug: normalizedSlug,
    });
    
    const isRecommended = book.recommendedBy?.some(recommender => {
      const normalizedRecommender = recommender.toLowerCase().trim().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
      const matches = normalizedRecommender === normalizedSlug;
      console.log(`  Comparing: "${normalizedRecommender}" with "${normalizedSlug}" = ${matches}`);
      return matches;
    });

    return isRecommended;
  });

  console.log('Debug - Final recommended books:', recommendedBooks);

  if (bitcoinersLoading || booksLoading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <p className="text-xl text-muted-foreground">Loading...</p>
      </div>
    );
  }

  if (!person) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <p className="text-xl text-muted-foreground">Person not found: {slug}</p>
      </div>
    );
  }

  return (
    <div className="-mx-6 -mt-6 min-h-[calc(100vh-4rem)]">
      <div className="border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
        <div className="container py-8">
          <div className="flex gap-8">
            <div className="w-48 h-48 relative bg-muted rounded-lg overflow-hidden">
              {person.avatarUrl ? (
                <img
                  src={person.avatarUrl}
                  alt={person.name}
                  className="object-cover w-full h-full"
                />
              ) : (
                <div className="absolute inset-0 flex items-center justify-center text-muted-foreground">
                  No image
                </div>
              )}
            </div>
            <div className="flex-1">
              <h1 className="text-3xl font-bold mb-4">{person.name}</h1>
              <div className="flex items-center gap-4 mb-4">
                {person.twitterHandle && (
                  <a
                    href={`https://twitter.com/${person.twitterHandle}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-muted-foreground hover:text-primary"
                  >
                    <XLogo className="h-5 w-5" />
                  </a>
                )}
              </div>
              <p className="text-muted-foreground whitespace-pre-wrap mb-4">
                {person.bio}
              </p>
            </div>
          </div>
        </div>
      </div>

      {recommendedBooks.length > 0 && (
        <div className="container py-8">
          <h2 className="text-2xl font-semibold mb-6">Recommended Books</h2>
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {recommendedBooks.map((book) => (
              <Link
                key={book.id}
                to={`/books/${book.slug}`}
                className="block"
              >
                <Card className="h-full overflow-hidden hover:shadow-lg transition-shadow">
                  <div className="aspect-[3/4] relative bg-muted">
                    {book.coverUrl ? (
                      <img
                        src={book.coverUrl}
                        alt={book.title}
                        className="object-cover w-full h-full"
                        loading="lazy"
                      />
                    ) : (
                      <div className="absolute inset-0 flex items-center justify-center text-muted-foreground">
                        No cover
                      </div>
                    )}
                  </div>
                  <CardContent className="p-4">
                    <h3 className="font-semibold mb-1">{book.title}</h3>
                    <p className="text-sm text-muted-foreground line-clamp-2">
                      {book.description}
                    </p>
                  </CardContent>
                </Card>
              </Link>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}